import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import api from '../lib/api';

const SacredSpaceTab = ({ spaceId, theme = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/campus/sacred-spaces/${spaceId}`)
      .then(res => setData(res.data))
      .catch(() => {});
  }, [spaceId]);

  const isDark = theme === 'dark';

  // Colors based on theme
  const tabBg = isDark
    ? 'rgba(30,25,15,0.75)'
    : 'rgba(245,237,216,0.85)';
  const tabBorder = isDark
    ? 'rgba(201,162,39,0.5)'
    : 'rgba(201,162,39,0.4)';
  const tabArrowColor = isDark
    ? 'rgba(212,175,55,1)'
    : 'rgba(139,115,64,0.9)';
  const panelBg = isDark
    ? 'rgba(10,10,18,0.92)'
    : 'rgba(250,248,244,0.95)';
  const panelBorder = isDark
    ? 'rgba(201,162,39,0.25)'
    : 'rgba(201,162,39,0.2)';
  const titleColor = isDark
    ? 'rgba(212,175,55,0.9)'
    : 'rgba(92,74,42,0.9)';
  const textColor = isDark
    ? 'rgba(255,253,248,0.7)'
    : 'rgba(92,74,42,0.65)';
  const boldColor = isDark
    ? 'rgba(212,175,55,0.85)'
    : 'rgba(139,115,64,0.9)';
  const dividerColor = isDark
    ? 'rgba(201,162,39,0.2)'
    : 'rgba(201,162,39,0.25)';

  const renderText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <p key={i} className="font-medium mt-5 mb-2 text-sm" style={{ color: boldColor }}>
            {line.replace(/\*\*/g, '')}
          </p>
        );
      }
      if (line.startsWith('—')) {
        return (
          <p key={i} className="text-[13px] leading-relaxed pl-3 py-0.5" style={{ color: textColor }}>
            {line}
          </p>
        );
      }
      if (line.trim() === '') {
        return <div key={i} className="h-3" />;
      }
      return (
        <p key={i} className="text-[13px] leading-[1.9] font-light" style={{ color: textColor }}>
          {line}
        </p>
      );
    });
  };

  return (
    <>
      <style>{`
        @keyframes tabPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes panelSlideIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes panelSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }
      `}</style>

      {/* === CLOSED TAB — vertical strip on left edge === */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="absolute left-0 top-0 h-full z-30 flex items-center group"
          style={{ width: '52px' }}
          data-testid={`sacred-tab-${spaceId}`}
        >
          <div
            className="h-[75%] w-full flex flex-col items-center justify-center gap-4 rounded-r-2xl transition-all duration-500 group-hover:w-[58px]"
            style={{
              background: tabBg,
              borderRight: `2px solid ${tabBorder}`,
              borderTop: `1.5px solid ${tabBorder}`,
              borderBottom: `1.5px solid ${tabBorder}`,
              backdropFilter: 'blur(16px)',
              boxShadow: `3px 0 25px rgba(0,0,0,0.15), inset -1px 0 0 ${isDark ? 'rgba(212,175,55,0.1)' : 'rgba(201,162,39,0.08)'}`,
            }}
          >
            <ChevronRight
              className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1"
              style={{ color: tabArrowColor, animation: 'tabPulse 2s ease-in-out infinite' }}
            />
            <div
              className="text-[11px] tracking-[0.25em] uppercase font-semibold"
              style={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                transform: 'rotate(180deg)',
                color: tabArrowColor,
              }}
            >
              Leer
            </div>
            <ChevronRight
              className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1"
              style={{ color: tabArrowColor, animation: 'tabPulse 2s ease-in-out infinite', animationDelay: '0.5s' }}
            />
          </div>
        </button>
      )}

      {/* === OPEN PANEL — slides from left === */}
      {open && (
        <div
          className="absolute left-0 top-0 h-full z-30 flex"
          style={{ animation: 'panelSlideIn 0.5s ease-out forwards' }}
          data-testid={`sacred-panel-${spaceId}`}
        >
          {/* Content panel */}
          <div
            className="h-full overflow-y-auto"
            style={{
              width: '340px',
              maxWidth: '85vw',
              background: panelBg,
              borderRight: `1px solid ${panelBorder}`,
              backdropFilter: 'blur(20px)',
              boxShadow: '4px 0 30px rgba(0,0,0,0.15)',
            }}
          >
            <div className="px-7 py-10">
              {/* Title */}
              <div className="mb-6">
                <div className="w-8 h-px mb-4" style={{ background: dividerColor }} />
                <h2
                  className="text-xs tracking-[0.25em] uppercase font-medium"
                  style={{ color: titleColor }}
                >
                  {data?.title || 'Espacio Sagrado'}
                </h2>
                <div className="w-8 h-px mt-4" style={{ background: dividerColor }} />
              </div>

              {/* Text body */}
              <div className="space-y-0">
                {renderText(data?.text || '')}
              </div>

              <div className="w-8 h-px mt-8" style={{ background: dividerColor }} />
            </div>
          </div>

          {/* Close tab — attached to the right edge of panel */}
          <button
            onClick={() => setOpen(false)}
            className="h-full flex items-center group"
            style={{ width: '52px' }}
            data-testid={`sacred-close-${spaceId}`}
          >
            <div
              className="h-[75%] w-full flex flex-col items-center justify-center gap-4 rounded-r-2xl transition-all duration-300"
              style={{
                background: tabBg,
                borderRight: `2px solid ${tabBorder}`,
                borderTop: `1.5px solid ${tabBorder}`,
                borderBottom: `1.5px solid ${tabBorder}`,
                backdropFilter: 'blur(16px)',
                boxShadow: '3px 0 25px rgba(0,0,0,0.15)',
              }}
            >
              <ChevronLeft
                className="w-6 h-6 transition-transform duration-300 group-hover:-translate-x-1"
                style={{ color: tabArrowColor }}
              />
              <div
                className="text-[11px] tracking-[0.25em] uppercase font-semibold"
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  color: tabArrowColor,
                }}
              >
                Cerrar
              </div>
              <ChevronLeft
                className="w-6 h-6 transition-transform duration-300 group-hover:-translate-x-1"
                style={{ color: tabArrowColor }}
              />
            </div>
          </button>
        </div>
      )}
    </>
  );
};

export default SacredSpaceTab;
